/**
 * ResizeHandle - Draggable handle between panels
 *
 * Features:
 * - Mouse and touch event support for mobile compatibility
 * - Visual feedback during drag
 * - Minimal hit area to avoid blocking auto-scroll
 *
 * @module components/ResizeHandle
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';

/**
 * ResizeHandle Component
 *
 * @param {Object} props
 * @param {Function} props.onResize - Callback with clientX position during drag
 * @param {'left'|'right'} props.position - Which side the handle is on (affects hit area direction)
 */
const ResizeHandle = ({ onResize, position = 'left' }) => {
  const [isDragging, setIsDragging] = useState(false);
  const handleRef = useRef(null);

  const startDrag = useCallback(e => {
    // Only handle left mouse button (button 0), let middle-click (button 1) pass through for auto-scroll
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    setIsDragging(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const handleMouseDown = useCallback(
    e => {
      // Only respond to left-click for resize, allow middle-click for auto-scroll
      if (e.button !== 0) return;
      startDrag(e);
    },
    [startDrag]
  );

  const handleTouchStart = useCallback(
    e => {
      startDrag(e);
    },
    [startDrag]
  );

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = e => {
      onResize(e.clientX);
    };

    const handleTouchMove = e => {
      if (e.touches.length > 0) {
        onResize(e.touches[0].clientX);
      }
    };

    const handleEnd = () => {
      setIsDragging(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
    document.addEventListener('touchcancel', handleEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
      document.removeEventListener('touchcancel', handleEnd);
    };
  }, [isDragging, onResize]);

  // Minimal hit area to avoid blocking auto-scroll
  // Only extends slightly in one direction to make grab easier without blocking scrolling
  const hitAreaClass =
    position === 'left'
      ? 'absolute inset-y-0 -left-0.5 right-0' // Extend left only (smaller to avoid blocking)
      : 'absolute inset-y-0 left-0 -right-0.5'; // Extend right only (smaller to avoid blocking)

  return (
    <div
      ref={handleRef}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      className={`
        w-1 hover:w-1.5 flex-shrink-0 cursor-col-resize group relative z-10
        transition-all duration-100 select-none touch-none
        ${
          isDragging
            ? 'bg-amber-500 w-1.5 shadow-lg shadow-amber-500/30'
            : 'bg-slate-200 dark:bg-neutral-700 hover:bg-amber-400 dark:hover:bg-amber-500'
        }
      `}
      style={{ touchAction: 'none' }}
    >
      {/* Visual grip indicator (only on hover) */}
      <div
        className={`
        absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
        flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity
        ${isDragging ? 'opacity-100' : ''}
      `}
      >
        <div className="w-0.5 h-0.5 rounded-full bg-white/80" />
        <div className="w-0.5 h-0.5 rounded-full bg-white/80" />
        <div className="w-0.5 h-0.5 rounded-full bg-white/80" />
      </div>

      {/* Hit area - only extends away from content panels */}
      <div className={hitAreaClass} />
    </div>
  );
};

export default ResizeHandle;
